<div class="container py-3">
  <div class="d-flex align-items-center mb-3">
    <h2 class="m-0">{{ title }}</h2>
    <button class="btn btn-link btn-sm p-0 text-primary ms-2"
            (click)="openHelp()"
            matTooltip="How to use PPK Downloader">
      <span class="material-icons" aria-hidden="true">menu_book</span>
    </button>
  </div>

  <ng-container *ngIf="selectedConnection as conn; else emptyState">
    <div class="mp-card">
      <div class="row g-3">
        <div class="col-12 col-lg-7">
          <div class="subsection">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <div class="d-flex align-items-center gap-2">
                <h4 class="subsection-title m-0">Record MountPoint Data</h4>
                <button class="btn btn-link btn-sm p-0 text-muted"
                        matTooltip="Schedule a backend task to download RTCM correction data from a configured NTRIP mount point.">
                  <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                </button>
              </div>
              <button class="btn btn-link btn-sm p-0 text-secondary text-decoration-none"
                      (click)="toggleQuickGuide()"
                      [attr.aria-expanded]="quickGuideExpanded">
                <span class="d-inline-flex align-items-center">
                  <span class="material-icons me-1 text-warning" style="font-size: 18px;" aria-hidden="true">menu_book</span>
                  <span class="me-1">Quick Guide</span>
                  <span class="material-icons" style="font-size: 20px;">
                    {{ quickGuideExpanded ? 'expand_less' : 'expand_more' }}
                  </span>
                </span>
              </button>
            </div>

            <!-- Quick Guide -->
            <div class="quick-start mb-3" *ngIf="quickGuideExpanded">
              <div class="quick-start-title mb-2">Steps:</div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">1</span>
                  <span class="step-text" matTooltip="Configure your NTRIP mount point connection settings first.">Setup MountPoint Connection</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepSetupStatus.tone === 'success', 'bg-info': stepSetupStatus.tone === 'info', 'bg-secondary': stepSetupStatus.tone === 'muted', 'bg-danger': stepSetupStatus.tone === 'danger' }">
                  {{ stepSetupStatus.label }}
                </span>
              </div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">2</span>
                  <span class="step-text">Select a Mount Point</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepMountPointStatus.tone === 'success', 'bg-info': stepMountPointStatus.tone === 'info', 'bg-secondary': stepMountPointStatus.tone === 'muted', 'bg-danger': stepMountPointStatus.tone === 'danger' }">
                  {{ stepMountPointStatus.label }}
                </span>
              </div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">3</span>
                  <span class="step-text" matTooltip="Must be at or after your current system time.">Set Recording Start Time</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepStartTimeStatus.tone === 'success', 'bg-info': stepStartTimeStatus.tone === 'info', 'bg-secondary': stepStartTimeStatus.tone === 'muted', 'bg-danger': stepStartTimeStatus.tone === 'danger' }">
                  {{ stepStartTimeStatus.label }}
                </span>
              </div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">4</span>
                  <span class="step-text">Set Recording Duration</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepDurationStatus.tone === 'success', 'bg-info': stepDurationStatus.tone === 'info', 'bg-secondary': stepDurationStatus.tone === 'muted', 'bg-danger': stepDurationStatus.tone === 'danger' }">
                  {{ stepDurationStatus.label }}
                </span>
              </div>
              <div class="quick-start-step">
                <div class="step-left">
                  <span class="step-num">5</span>
                  <span class="step-text">Submit & Refresh to Download</span>
                </div>
                <span class="badge"
                      [ngClass]="{ 'bg-success': stepSubmitStatus.tone === 'success', 'bg-info': stepSubmitStatus.tone === 'info', 'bg-secondary': stepSubmitStatus.tone === 'muted', 'bg-danger': stepSubmitStatus.tone === 'danger' }">
                  {{ stepSubmitStatus.label }}
                </span>
              </div>
            </div>

            <div class="mb-2">
              <div class="small mb-1 d-inline-flex align-items-center">
                <span>Select a Mount Point:</span>
                <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                        matTooltip="Pick the CORS mount point to record RTCM correction data from.">
                  <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                </button>
              </div>
              <div class="d-flex align-items-center">
                <div class="mp-select-wrapper" style="min-width: 280px;">
                  <select class="form-select form-select-sm"
                          [ngModel]="getConnectionKey(currentConnection)"
                          (ngModelChange)="selectFromDropdown($event)">
                    <option value=""></option>
                    <option *ngFor="let c of sortedConnections" [value]="getConnectionKey(c)">
                      {{ c.mountPoint }}
                    </option>
                  </select>
                </div>
                <button class="btn btn-outline-secondary btn-sm ms-2" type="button"
                        (click)="openMountpointConnectionSetup()"
                        matTooltip="Add or edit your mount point connection settings.">
                  <span class="d-inline-flex align-items-center">
                    <span class="material-icons me-1" style="font-size: 1.1rem;">settings</span>
                    <span>MountPoint Setup</span>
                  </span>
                </button>
              </div>
            </div>
            <div class="row g-2">
              <div class="col-md-5">
                <label class="form-label small d-inline-flex align-items-center">
                  <span>Recording Start Time</span>
                  <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                          type="button"
                          (click)="openStartTimeHelp()"
                          matTooltip="Recording Start Time rules"
                          aria-label="Recording Start Time rules">
                    <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                  </button>
                </label>
                <input type="datetime-local" class="form-control form-control-sm"
                       [ngModel]="getPlanStartAtInput(conn)"
                       (ngModelChange)="onPlanStartAtInputChange(conn, $event)"
                       [attr.min]="planStartAtMinLocal"
                       [attr.max]="planStartAtMaxLocal"
                       (keydown)="$event.preventDefault()" (keypress)="$event.preventDefault()" (paste)="$event.preventDefault()">
              </div>
              <div class="col-md-4">
                <label class="form-label small d-inline-flex align-items-center">
                  <span>Recording Duration</span>
                  <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                          matTooltip="How long to record RTCM correction data.">
                    <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                  </button>
                </label>
                <select class="form-select form-select-sm"
                        [ngModel]="getDurationSelection(conn)"
                        (ngModelChange)="onDurationSelectionChange(conn, $event)">
                  <option *ngFor="let opt of durationPresetOptions" [ngValue]="opt.minutes">{{ opt.label }}</option>
                </select>
              </div>
            </div>
            <div class="d-flex align-items-center mt-3">
              <button class="btn btn-sm btn-primary"
                      (click)="onSubmitRecording(conn)"
                      [disabled]="isSubmitting(conn) || isRecorderRunningOrScheduled(conn) || isStartTimeRejected(conn) || !isStartTimeValid(downInputs(conn).planStartAt)"
                      matTooltip="Schedules the recording. You can leave this page; the task runs in backend.">
                <span class="d-inline-flex align-items-center">
                  <span class="material-icons me-1" style="font-size: 1.1rem;">send</span>
                  <span>Submit the Task</span>
                </span>
              </button>
            </div>
          </div>

          <div class="record-card mt-3">
            <div class="file-card">
              <div class="d-flex justify-content-between align-items-center mb-2">
                <div class="subsection-title m-0 d-inline-flex align-items-center">
                  <span>Downloaded File:</span>
                  <button class="btn btn-link btn-sm p-0 ms-1 text-muted"
                          matTooltip="Click Refresh to check status and get the download link when ready.">
                    <span class="material-icons" style="font-size: 16px; vertical-align: middle;">info</span>
                  </button>
                </div>
                <button class="btn btn-sm btn-outline-primary"
                        type="button"
                        (click)="onRefreshStatus(conn)"
                        matTooltip="Refresh status"
                        aria-label="Refresh status">
                  <span class="d-inline-flex align-items-center">
                    <span class="material-icons me-1" style="font-size: 1.1rem;" aria-hidden="true">refresh</span>
                    <span>Refresh</span>
                  </span>
                </button>
              </div>
              <div class="file-card-row">
                <ng-container *ngIf="downloaderUrlByMountpoint[conn.mountPoint] as dl; else dlEmpty">
                  <ng-container *ngIf="!dl?.running; else runningTpl">
                    <ng-container *ngIf="dl?.url; else noFileTpl">
                      <div class="w-100 me-3">
                        <div class="d-flex align-items-center justify-content-between gap-3">
                          <a class="fw-semibold link-primary"
                             [href]="isExpired(dl) ? null : dl.url"
                             [class.disabled]="isExpired(dl)"
                             [attr.aria-disabled]="isExpired(dl) ? true : null"
                             (click)="isExpired(dl) ? $event.preventDefault() : null"
                             target="_blank" rel="noopener">
                            rtcm_data.rtcm3
                          </a>
                          <div class="text-muted fw-semibold flex-shrink-0">
                            {{ dl.lastModified ? (dl.lastModified | date:'medium') : '--' }}
                          </div>
                        </div>
                        <ng-template #expiredTpl>
                          <div class="text-primary small mt-1">(This file has expired and is no longer available for download.)</div>
                        </ng-template>
                        <div *ngIf="isExpired(dl); else okTpl">
                          <ng-container [ngTemplateOutlet]="expiredTpl"></ng-container>
                        </div>
                        <ng-template #okTpl></ng-template>
                      </div>
                    </ng-container>
                    <ng-template #noFileTpl>
                      <div class="text-muted">No downloaded file yet. Submit a task above, then click Refresh to check status. Please make sure the mountpoint you download from is up and running.</div>
                    </ng-template>
                  </ng-container>
                  <ng-template #runningTpl>
                    <div class="text-primary small">A recorder is currently running or scheduled. Please check back later.</div>
                  </ng-template>
                </ng-container>
                <ng-template #dlEmpty>
                  <div class="text-muted">No downloaded file yet. Submit a task above, then click Refresh to check status. Please make sure the mountpoint you download from is up and running.</div>
                </ng-template>
              </div>
            </div>
          </div>
        </div>
        <div class="col-12 col-lg-5 d-flex align-items-start">
          <img src="assets/images/landing/GeoAstra_PPK_Downloader_diagram.drawio.png"
               alt="PPK Downloader Diagram"
               class="img-fluid w-100 rounded border diagram-scale-80 diagram-align-top">
        </div>
      </div>
    </div>
  </ng-container>
  <ng-template #emptyState>
    <div class="mp-card">
      <div class="subsection">
        <div class="d-flex align-items-center justify-content-between flex-wrap gap-2">
          <div>
            <div class="subsection-title">No mount point connections yet</div>
            <div class="text-muted small mt-1">
              Click <strong>MountPoint Setup</strong> to add your first mount point connection.
            </div>
          </div>
          <button class="btn btn-primary btn-sm" type="button" (click)="openMountpointConnectionSetup()">
            <span class="d-inline-flex align-items-center">
              <span class="material-icons me-1" style="font-size: 1.1rem;">settings</span>
              <span>MountPoint Setup</span>
            </span>
          </button>
        </div>
        <div class="text-muted small mt-2" *ngIf="isLoading">
          Loading connections...
        </div>
        <div class="text-danger small mt-2" *ngIf="!isLoading && errorMessage">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </ng-template>
</div>


